import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Select, Input, Row, Col, Card, Tag, Space, Popconfirm, Tooltip } from 'antd';
import {
	PlusOutlined,
	EditOutlined,
	DeleteOutlined,
	EyeOutlined,
	CopyOutlined,
	ReloadOutlined,
} from '@ant-design/icons';
import { AiOutlineSearch } from 'react-icons/ai';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { deleteApi } from 'redux/sagas/deleteApiSaga';
import { postApi } from 'redux/sagas/postApiDataSaga';
import { SERVER_IP } from 'assets/Config';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import { formQueryStringFromObject } from 'helpers';
import { useI18n } from 'hooks/useI18n';
import {
	getWorkoutGenderOptions,
	getWorkoutGoalOptions,
	getWorkoutLevelOptions,
	translateWorkoutGender,
	translateWorkoutGoal,
	translateWorkoutLevel,
} from 'helpers/workoutI18n';

import './index.scss';

const LEVEL_CLASS_MAP = {
	Beginner: 'template-list__tag--beginner',
	Intermediate: 'template-list__tag--intermediate',
	Advanced: 'template-list__tag--advanced',
};

const GOAL_CLASS_MAP = {
	Strength: 'template-list__tag--strength',
	'Fat Loss': 'template-list__tag--fat-loss',
	Endurance: 'template-list__tag--endurance',
	Flexibility: 'template-list__tag--flexibility',
	'Muscle Gain': 'template-list__tag--muscle-gain',
};

const GENDER_CLASS_MAP = {
	Male: 'template-list__tag--male',
	Female: 'template-list__tag--female',
	Unisex: 'template-list__tag--unisex',
};

const SKELETON_CARD_COUNT = 6;

const TemplateCardSkeleton = ({ index }) => (
	<Col key={`template-skeleton-${index}`} xs={24} sm={12} xl={8}>
		<Card className="template-list-card template-list-card--skeleton">
			<div className="template-list-card__header">
				<div className="template-list-card__copy template-list-card__copy--skeleton">
					<div className="template-list-card__title template-list-card__title--skeleton">
						<span className="template-list-skeleton template-list-skeleton--title" />
						<span className="template-list-skeleton template-list-skeleton--title template-list-skeleton--title-secondary" />
					</div>
					<div className="template-list-card__subtitle template-list-card__subtitle--skeleton">
						<span className="template-list-skeleton template-list-skeleton--subtitle" />
						<span className="template-list-skeleton template-list-skeleton--subtitle template-list-skeleton--subtitle-secondary" />
					</div>
				</div>
				<span className="template-list-skeleton template-list-skeleton--tag" />
			</div>
			<div className="template-list-card__overview">
				<div className="template-list-card__metric template-list-card__metric--skeleton">
					<span className="template-list-skeleton template-list-skeleton--metric-value" />
					<span className="template-list-skeleton template-list-skeleton--metric-label" />
				</div>
				<div className="template-list-card__meta template-list-card__meta--skeleton">
					<div className="template-list-card__meta-item">
						<span className="template-list-skeleton template-list-skeleton--meta-label" />
						<span className="template-list-skeleton template-list-skeleton--meta-value" />
					</div>
					<div className="template-list-card__meta-item">
						<span className="template-list-skeleton template-list-skeleton--meta-label" />
						<span className="template-list-skeleton template-list-skeleton--meta-value template-list-skeleton--meta-value-wide" />
					</div>
				</div>
			</div>
			<div className="customer-action-group template-list-card__actions">
				<span className="template-list-skeleton template-list-skeleton--action" />
				<span className="template-list-skeleton template-list-skeleton--action" />
				<span className="template-list-skeleton template-list-skeleton--action" />
				<span className="template-list-skeleton template-list-skeleton--action" />
			</div>
		</Card>
	</Col>
);

const TemplateList = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { t } = useI18n();
	const globalRedux = useSelector((state) => state.globalRedux);
	const workoutTemplateRedux = useSelector((state) => state.workoutTemplateRedux);

	const [searchKey, setSearchKey] = useState('');
	const [genderFilter, setGenderFilter] = useState('');
	const [levelFilter, setLevelFilter] = useState('');
	const [goalFilter, setGoalFilter] = useState('');
	const genderOptions = useMemo(() => getWorkoutGenderOptions(t, 'workouts.enums.gender.all'), [t]);
	const levelOptions = useMemo(() => getWorkoutLevelOptions(t, 'workouts.enums.level.all_levels'), [t]);
	const goalOptions = useMemo(() => getWorkoutGoalOptions(t, 'workouts.enums.goal.all_goals'), [t]);
	const isLoading = globalRedux.apiStatus.GET_WORKOUT_TEMPLATES === 'PENDING';

	const tableData = Array.isArray(workoutTemplateRedux?.list) ? workoutTemplateRedux.list : [];

	const fetchTemplates = useCallback(() => {
		const params = formQueryStringFromObject({
			branchId: globalRedux?.selectedBranch?.id,
			gender: genderFilter,
			level: levelFilter,
			goal: goalFilter,
		});
		dispatch(getApi('GET_WORKOUT_TEMPLATES', `${SERVER_IP}api/workoutTemplate?${params}`));
	}, [dispatch, globalRedux?.selectedBranch?.id, genderFilter, levelFilter, goalFilter]);

	useEffect(() => {
		fetchTemplates();
	}, [fetchTemplates]);

	useEffect(() => {
		if (globalRedux.apiStatus.DELETE_WORKOUT_TEMPLATE === 'SUCCESS') {
			dispatch(resetApiStatus('DELETE_WORKOUT_TEMPLATE'));
			fetchTemplates();
		}
		if (globalRedux.apiStatus.CLONE_WORKOUT_TEMPLATE === 'SUCCESS') {
			dispatch(resetApiStatus('CLONE_WORKOUT_TEMPLATE'));
			fetchTemplates();
		}
	}, [globalRedux.apiStatus.DELETE_WORKOUT_TEMPLATE, globalRedux.apiStatus.CLONE_WORKOUT_TEMPLATE, dispatch, fetchTemplates]);

	const handleDelete = (record) => {
		dispatch(deleteApi('DELETE_WORKOUT_TEMPLATE', `${SERVER_IP}api/workoutTemplate/${record.id}`));
	};

	const handleClone = (record) => {
		dispatch(postApi({}, 'CLONE_WORKOUT_TEMPLATE', `${SERVER_IP}api/workoutTemplate/${record.id}/clone`));
	};

	const filteredData = useMemo(() => {
		let data = tableData;
		if (genderFilter) data = data.filter((t) => t.gender === genderFilter);
		if (levelFilter) data = data.filter((t) => t.level === levelFilter);
		if (goalFilter) data = data.filter((t) => t.goal === goalFilter);
		if (searchKey) data = data.filter((t) => t.name?.toLowerCase().includes(searchKey.toLowerCase()));
		return data;
	}, [tableData, genderFilter, levelFilter, goalFilter, searchKey]);

	const templateCountLabel = filteredData.length === 1 ? t('workouts.template.list.template_one') : t('workouts.template.list.template_other');

	return (
		<div className="template-list-page">
			<div className="template-list-page__shell">
				<div className="template-list-toolbar">
					<div className="template-list-toolbar__copy">
						<span className="template-list-toolbar__eyebrow">{t('workouts.template.list.eyebrow')}</span>
						<h1 className="template-list-toolbar__title">{t('workouts.template.list.title')}</h1>
						<p className="template-list-toolbar__subtitle">{t('workouts.template.list.subtitle')}</p>
					</div>
					<Row gutter={[12, 12]} className="template-list-toolbar__controls" align="middle" justify="space-between">
						<Col flex="auto">
							<Row gutter={[10, 10]} align="middle">
								<Col xs={24} sm={12} lg={6}>
									<Input
										className="template-list-toolbar__search"
										placeholder={t('workouts.template.list.search_placeholder')}
										suffix={<AiOutlineSearch />}
										value={searchKey}
										allowClear
										onChange={(e) => setSearchKey(e.target.value)}
									/>
								</Col>
								<Col xs={24} sm={12} lg={5}>
									<Select className="template-list-toolbar__select" options={genderOptions} value={genderFilter} onChange={setGenderFilter} placeholder={t('workouts.form.fields.gender')} />
								</Col>
								<Col xs={24} sm={12} lg={5}>
									<Select className="template-list-toolbar__select" options={levelOptions} value={levelFilter} onChange={setLevelFilter} placeholder={t('workouts.form.fields.level')} />
								</Col>
								<Col xs={24} sm={12} lg={5}>
									<Select className="template-list-toolbar__select" options={goalOptions} value={goalFilter} onChange={setGoalFilter} placeholder={t('workouts.template.metadata.goal')} />
								</Col>
								<Col>
									<div className="template-list-toolbar__results-chip">
										<strong>{filteredData.length}</strong>
										<span>{templateCountLabel}</span>
									</div>
								</Col>
							</Row>
						</Col>
						<Col>
							<Space className="template-list-toolbar__actions" size={10}>
								<Button
									className="template-list-toolbar__reload-btn"
									icon={<ReloadOutlined />}
									loading={globalRedux.apiStatus.GET_WORKOUT_TEMPLATES === 'PENDING'}
									onClick={fetchTemplates}
								/>
								<Button className="template-list-toolbar__primary-btn" type="primary" icon={<PlusOutlined />} onClick={() => navigate('/workout-templates/create')}>
									{t('workouts.template.list.create_template')}
								</Button>
							</Space>
						</Col>
					</Row>
				</div>
				<Row gutter={[16, 16]} className="template-list-grid">
					{isLoading
						? Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => <TemplateCardSkeleton key={index} index={index} />)
						: filteredData.map((template) => (
						<Col key={template.id} xs={24} sm={12} xl={8}>
							<Card
								className="template-list-card"
							>
								<div className="template-list-card__header">
									<div className="template-list-card__copy">
										<div className="template-list-card__title">{template.name}</div>
										<div className="template-list-card__subtitle">{t('workouts.template.list.card_subtitle', { gender: translateWorkoutGender(t, template.gender, t('workouts.enums.gender.all_members')) })}</div>
									</div>
									<Tag className={`template-list__tag ${LEVEL_CLASS_MAP[template.level] || 'template-list__tag--neutral'}`}>{translateWorkoutLevel(t, template.level, t('workouts.enums.level.general'))}</Tag>
								</div>
								<div className="template-list-card__overview">
									<div className="template-list-card__metric">
										<span className="template-list-card__metric-value">{template.durationDays || 0}</span>
										<span className="template-list-card__metric-label">{t('workouts.template.metadata.days')}</span>
									</div>
									<div className="template-list-card__meta">
										<div className="template-list-card__meta-item">
											<span className="template-list-card__meta-label">{t('workouts.form.fields.gender')}</span>
											<strong className="template-list-card__meta-value">{translateWorkoutGender(t, template.gender, t('workouts.enums.gender.all'))}</strong>
										</div>
										<div className="template-list-card__meta-item">
											<span className="template-list-card__meta-label">{t('workouts.template.metadata.goal')}</span>
											<strong className="template-list-card__meta-value">{translateWorkoutGoal(t, template.goal, t('workouts.enums.goal.general_goal'))}</strong>
										</div>
									</div>
								</div>
								<div className="customer-action-group template-list-card__actions">
									<Tooltip title={t('workouts.template.list.preview_template')}>
										<Button type="text" className="customer-action-btn customer-action-btn--workout" icon={<EyeOutlined />} onClick={() => navigate(`/workout-templates/${template.id}/preview`)} aria-label="Preview template" />
									</Tooltip>
									<Tooltip title={t('workouts.template.list.edit_template')}>
										<Button type="text" className="customer-action-btn customer-action-btn--edit" icon={<EditOutlined />} onClick={() => navigate(`/workout-templates/edit/${template.id}`)} aria-label="Edit template" />
									</Tooltip>
									<Popconfirm title={t('workouts.template.list.clone_confirm')} onConfirm={() => handleClone(template)} okText={t('common.yes')} cancelText={t('common.no')}>
										<Button type="text" className="customer-action-btn template-list-card__clone-btn" icon={<CopyOutlined />} aria-label="Clone template" title={t('workouts.template.list.clone_template')} />
									</Popconfirm>
									<Popconfirm title={t('workouts.template.list.delete_confirm')} onConfirm={() => handleDelete(template)} okText={t('common.yes')} cancelText={t('common.no')}>
										<Button type="text" className="customer-action-btn customer-action-btn--delete" icon={<DeleteOutlined />} aria-label="Delete template" title={t('workouts.template.list.delete_template')} />
									</Popconfirm>
								</div>
							</Card>
						</Col>
					))}
					{!isLoading && filteredData.length === 0 && (
						<Col xs={24}>
							<div className="template-list-empty-state">
								<div className="template-list-empty-state__title">{t('workouts.template.list.no_templates_title')}</div>
								<div className="template-list-empty-state__copy">{t('workouts.template.list.no_templates_copy')}</div>
							</div>
						</Col>
					)}
				</Row>
			</div>
		</div>
	);
};

export default TemplateList;
